import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Column } from "react-table";
import { CellProps } from "react-table";

import style from "./buyback-tradein-view.scss";
import BuybackDetailBox from "./BuybackDetails";
import Table, { deriveColumnWidth } from "components/table/Table";
import TextWithTooltip from "components/table/TextWithTooltip";
import Heading from "components/typography/heading/Heading";
import { TOOLTIP_DELAY } from "domain/globalConstants";
import { DiagnosticReportResponse, Estimate, Quote } from "domain/reports";
import layoutStyle from "styles/layout.scss";
import { currency, formatTimestamp } from "utils/format";
import { RepositoryKey } from "utils/repository";

import checkIcon from "assets/images/icons/checkMarkInCircle.svg";
import crossIcon from "assets/images/icons/cross.svg";

import testIds from "testIds.json";

export interface BuybackTradeinDetailsProps {
    diagnosticReportResponse: DiagnosticReportResponse;
}

function getLatestByChannel<T extends { channel: string; date: string }>(data: T[] = []): T[] {
    const latestByChannel = new Map<string, T>();

    for (const item of data) {
        const existing = latestByChannel.get(item.channel);
        if (!existing || new Date(item.date) > new Date(existing.date)) {
            latestByChannel.set(item.channel, item);
        }
    }

    return Array.from(latestByChannel.values());
}

export default function BuybackTradeinView({ diagnosticReportResponse }: BuybackTradeinDetailsProps): JSX.Element {
    const { t } = useTranslation();
    const tableContainerRef = React.useRef<HTMLDivElement>(null);
    const tableHeight = 240;

    const latestEstimates = React.useMemo(
        () => getLatestByChannel(diagnosticReportResponse?.buybackTradein?.estimate),
        [diagnosticReportResponse?.buybackTradein?.estimate]
    );

    const latestQuotes = React.useMemo(
        () => getLatestByChannel(diagnosticReportResponse?.buybackTradein?.quote),
        [diagnosticReportResponse?.buybackTradein?.quote]
    );

    const getIcon = (result: boolean | undefined): JSX.Element => {
        const icon = result ? checkIcon : crossIcon;
        return icon ? (
            <img src={icon} data-delay-show={TOOLTIP_DELAY} alt={result ? "Eligible" : "Ineligible"} />
        ) : (
            <></>
        );
    };

    function removeSemiColon(text: string): string {
        return text.replace(/:/g, "");
    }

    function getColumns<
        T extends {
            eligible?: boolean;
            price?: number;
            currency?: string;
            channel?: string;
            date?: string;
            tradeId?: string;
            expiration?: string;
        },
    >(columnWidth: number, tableContainerRef: React.RefObject<HTMLDivElement>): Column<T>[] {
        return [
            {
                Header: [
                    <TextWithTooltip
                        text={removeSemiColon(t("DiagnosticReportsTable.buyBackTradeInDetail.eligible"))}
                        key="1"
                    />,
                ],
                accessor: "eligible" as keyof T,
                Cell: ({ value }: CellProps<T, boolean | undefined>) => (
                    <div className={style.identifer}>
                        <div className={style.icon}>{getIcon(value)}</div>
                    </div>
                ),
                width: deriveColumnWidth(columnWidth, tableContainerRef),
            },
            {
                Header: [
                    <TextWithTooltip
                        text={removeSemiColon(t("DiagnosticReportsTable.buyBackTradeInDetail.value"))}
                        key="2"
                    />,
                ],
                accessor: "price" as keyof T,
                Cell: ({ row }: CellProps<T, number>) => (
                    <TextWithTooltip text={currency(row.original.price!, row.original.currency!)} />
                ),
                width: deriveColumnWidth(columnWidth, tableContainerRef),
            },
            {
                Header: [
                    <TextWithTooltip
                        text={removeSemiColon(t("DiagnosticReportsTable.buyBackTradeInDetail.channel"))}
                        key="3"
                    />,
                ],
                accessor: "channel" as keyof T,
                Cell: ({ cell: { value } }: CellProps<T, string>) => <TextWithTooltip text={value} />,
                width: deriveColumnWidth(columnWidth, tableContainerRef),
            },
            {
                Header: [
                    <TextWithTooltip
                        text={removeSemiColon(t("DiagnosticReportsTable.buyBackTradeInDetail.date"))}
                        key="4"
                    />,
                ],
                accessor: "date" as keyof T,
                Cell: ({ cell: { value } }: CellProps<T, string>) => <TextWithTooltip text={formatTimestamp(value)} />,
                width: deriveColumnWidth(columnWidth, tableContainerRef),
            },
            {
                Header: [
                    <TextWithTooltip
                        text={removeSemiColon(t("DiagnosticReportsTable.buyBackTradeInDetail.tradeId"))}
                        key="5"
                    />,
                ],
                accessor: "tradeId" as keyof T,
                Cell: ({ cell: { value } }: CellProps<T, string>) => <TextWithTooltip text={value} />,
                width: deriveColumnWidth(columnWidth, tableContainerRef),
            },
            {
                Header: [
                    <TextWithTooltip
                        text={removeSemiColon(t("DiagnosticReportsTable.buyBackTradeInDetail.offerExpiry"))}
                        key="6"
                    />,
                ],
                accessor: "expiration" as keyof T,
                Cell: ({ cell: { value } }: CellProps<T, string | undefined>) => (
                    <TextWithTooltip text={typeof value !== "undefined" ? formatTimestamp(value) : ""} />
                ),
                width: deriveColumnWidth(columnWidth, tableContainerRef),
            },
        ] as Column<T>[];
    }

    return (
        <>
            <Heading tag="div" variant="SUBTITLE_1">
                {t("DiagnosticReportsTable.buyBackTradeInDetail.estimation")}
            </Heading>
            {latestEstimates.map((estimate, index) => (
                <BuybackDetailBox
                    key={index}
                    value={estimate.price}
                    currencyCode={estimate.currency}
                    eligible={estimate.eligible}
                    date={estimate.date}
                    channel={estimate.channel}
                    isQuote={false}
                />
            ))}

            <Heading tag="div" variant="SUBTITLE_1">
                {t("DiagnosticReportsTable.buyBackTradeInDetail.quotation")}
            </Heading>
            {latestQuotes.map((quote, index) => (
                <BuybackDetailBox
                    key={index}
                    value={quote.price}
                    currencyCode={quote.currency}
                    eligible={quote.eligible}
                    date={quote.date}
                    channel={quote.channel}
                    tradeId={quote.tradeId}
                    expiration={quote.expiration}
                    isQuote={true}
                />
            ))}

            <div className={classNames(layoutStyle.tableWrapper)} ref={tableContainerRef}>
                <Heading tag="div" variant="SUBTITLE_1">
                    {t("DiagnosticReportsTable.buyBackTradeInDetail.estimationHistory")}
                </Heading>
                <Table
                    tableIdentity={RepositoryKey.BBTI_ESTIMATE_TABLE}
                    data={diagnosticReportResponse.buybackTradein?.estimate || []}
                    columns={getColumns<Estimate>(23, tableContainerRef)}
                    loaded={typeof diagnosticReportResponse.buybackTradein.estimate !== "undefined"}
                    tooltips={true}
                    emptyMessage={t("DiagnosticReportsTable.diagnosticSummary.emptyStateMessage")}
                    testId={testIds.workArea.report.diagnostic.reportDialog.buybackTradeinDetail.itself}
                    dialogHeight={tableHeight}
                    hiddenColumns={["expiration", "tradeId"]}
                    isView={false}
                />
            </div>

            <div className={layoutStyle.tableWrapper} ref={tableContainerRef}>
                <Heading tag="div" variant="SUBTITLE_1">
                    {t("DiagnosticReportsTable.buyBackTradeInDetail.quotationHistory")}
                </Heading>
                <Table
                    tableIdentity={RepositoryKey.BBTI_QUOTE_TABLE}
                    data={diagnosticReportResponse.buybackTradein?.quote || []}
                    columns={getColumns<Quote>(10, tableContainerRef)}
                    loaded={typeof diagnosticReportResponse.buybackTradein.quote !== "undefined"}
                    tooltips={true}
                    emptyMessage={t("DiagnosticReportsTable.diagnosticSummary.emptyStateMessage")}
                    testId={testIds.workArea.report.diagnostic.reportDialog.buybackTradeinDetail.itself}
                    dialogHeight={tableHeight}
                    isView={false}
                />
            </div>
        </>
    );
}
